/* ALARMS AT THE BEDSIDE, in the Philips extended form: stars for the priority, the parameter, the number, the limit it
   crossed (`** SpO2 94 < 96`). Three levels only: high (three stars, red), medium (two stars, yellow) and low (one star, cyan),
   and a low one is a technical condition: the one the world models is a probe that slips (episode.ts: "poor signal quality").
   The console draws no INOP the world does not model (no leads off, no cuff inflating): nothing here invents a condition.

   Where every number comes from:
   - the alarm log is the world's own (get_chart's `alarms` section: t, source, level, text), read only when the agent reads it;
   - the limits are the world's defaults (people.ts DEFAULT_LIMITS, checked against that file by alarms.test.ts) with every
     successful set_alarm_limits call of this case laid over them, as the episode applies them;
   - a number charted by the nurse is compared with those limits, and a crossing reads at the medium level (the world's
     "warning"); only the world's own log says high. */

export type LimitKey = 'hr_low' | 'hr_high' | 'map_low' | 'map_high' | 'spo2_low' | 'rr_low' | 'rr_high'
export type AlarmLimits = Record<LimitKey, number>

/** worlds/001/src/people.ts DEFAULT_LIMITS: the limits a case starts with. */
export const DEFAULT_LIMITS: AlarmLimits = { hr_low: 40, hr_high: 140, map_low: 60, map_high: 120, spo2_low: 88, rr_low: 8, rr_high: 35 }

export type Level = 'high' | 'medium' | 'low'
export const STARS: Record<Level, string> = { high: '***', medium: '**', low: '*' }
export const LEVEL_NAME: Record<Level, string> = { high: 'high priority', medium: 'medium priority', low: 'technical condition' }

/** One entry of the world's alarm log. */
export type LogAlarm = { t: number; source: string; level: string; text: string }

/** The calls of this case, as the page holds them: the tool, its arguments and whether the world accepted it. */
export type PlacedCall = { tool: string; args: Record<string, unknown>; ok: boolean }

const KEYS = Object.keys(DEFAULT_LIMITS) as LimitKey[]

/** The limits in force after the calls so far: each accepted set_alarm_limits lays its finite numbers over the last, as the episode does. */
export function effectiveLimits(calls: readonly PlacedCall[]): AlarmLimits {
  const out = { ...DEFAULT_LIMITS }
  for (const c of calls) {
    if (c.tool !== 'set_alarm_limits' || !c.ok) continue
    for (const k of KEYS) {
      const v = c.args[k]
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
    }
  }
  return out
}

export type Alarmed = 'hr' | 'map' | 'spo2' | 'rr'
export const LABEL: Record<Alarmed, string> = { hr: 'HR', map: 'MAP', spo2: 'SpO2', rr: 'RR' }

/** The two limits of a parameter; `spo2` has a floor only. */
export function limitsOf(p: Alarmed, l: AlarmLimits): { low: number; high: number | null } {
  return p === 'hr' ? { low: l.hr_low, high: l.hr_high } : p === 'map' ? { low: l.map_low, high: l.map_high } : p === 'rr' ? { low: l.rr_low, high: l.rr_high } : { low: l.spo2_low, high: null }
}

/** `** SpO2 94 < 96` for a number outside a limit, at the level given; null when the number is inside both. */
export function crossedLimit(p: Alarmed, value: number, l: AlarmLimits, level: Level = 'medium', label: string = LABEL[p]): string | null {
  const lim = limitsOf(p, l)
  if (value < lim.low) return `${STARS[level]} ${label} ${value} < ${lim.low}`
  if (lim.high !== null && value > lim.high) return `${STARS[level]} ${label} ${value} > ${lim.high}`
  return null
}

const PARSE: { re: RegExp; param: Alarmed; label: (m: RegExpExecArray) => string }[] = [
  { re: /^HR (\d+)/, param: 'hr', label: () => 'HR' },
  { re: /^(ART|NIBP) mean (\d+)/, param: 'map', label: (m) => m[1] },
  { re: /^SpO2 (\d+)%/, param: 'spo2', label: () => 'SpO2' },
  { re: /^RR (\d+)/, param: 'rr', label: () => 'RR' },
]

/** A log entry's priority. The world's "crisis" is high and its "warning" medium; a probe that slips is a technical condition, whatever level it carries. */
export function levelOf(a: Pick<LogAlarm, 'level' | 'text'>): Level {
  if (/poor signal quality/.test(a.text)) return 'low'
  return a.level === 'crisis' ? 'high' : 'medium'
}

/** An alarm log entry as the monitor prints it: stars, then the parameter, the number and the limit it crossed under the limits now in force. A condition with no limit (a ventilator pressure, a probe) prints the world's own words. */
export function alarmLine(a: LogAlarm, l: AlarmLimits): { level: Level; text: string } {
  const level = levelOf(a)
  if (level !== 'low') {
    for (const p of PARSE) {
      const m = p.re.exec(a.text)
      if (!m) continue
      const value = Number(m[m.length - 1])
      const label = p.label(m)
      return { level, text: crossedLimit(p.param, value, l, level, label) ?? `${STARS[level]} ${label} ${value}` }
    }
  }
  return { level, text: `${STARS[level]} ${a.text}` }
}

/** Alarms per hour from the log as read: the count in the last 60 minutes of the case, said as a count in fewer minutes while the case is younger than an hour. */
export function alarmsPerHour(log: readonly LogAlarm[], t: number): { count: number; windowMin: number; text: string } {
  const windowMin = Math.max(0, Math.min(60, Math.round(t)))
  const count = log.filter((a) => a.t > t - 60 && a.t <= t).length
  return { count, windowMin, text: windowMin >= 60 ? `${count}/h` : `${count} in ${windowMin} min` }
}
